import { Injectable } from '@angular/core';
import { ConfigurationService } from '../configuration/configuration.service';
import { API, API_V2, ApiEndpoints, ApiEntities } from '../../../shared/constants/api.constants';

@Injectable({
  providedIn: 'root'
})
export class UrlService {

  constructor(private appConfig: ConfigurationService) { }

  private get baseUrl(): string {
    return `${this.appConfig.getConfig()}${API}${API_V2}`;
  }

  getAdminsUrl(): string {
    return this.baseUrl + ApiEndpoints.ADMINS;
  }

  getAdminByIdUrl(adminId: string): string {
    return this.getAdminsUrl() + '/' + adminId;
  }

  getAvailabilitiesUrl(technicianId: string): string {
    return this.getTechnicianByIdUrl(technicianId) + ApiEndpoints.AVAILABILITIES;
  }

  getAvailabilityByDateUrl(technicianId: string): string {
    return this.getAvailabilitiesUrl(technicianId) + ApiEndpoints.AVAILABILITIES + ApiEntities.BY_DATE;
  }

  getAvailabilityByIdUrl(technicianId: string, availabilityId: string): string {
    return this.getAvailabilitiesUrl(technicianId) + '/' + availabilityId;
  }

  getChatbotUrl(): string {
    return this.baseUrl + ApiEndpoints.CHATBOTS;
  }

  getChatbotByIdUrl(chatbotId: string): string {
    return this.getChatbotUrl() + '/' + chatbotId;
  }

  getChatbotTestUrl(): string {
    return this.baseUrl + ApiEndpoints.CHATBOT_TEST;
  }

  getChatbotTestByIdUrl(chatbotTestId: string): string {
    return this.getChatbotTestUrl() + '/' + chatbotTestId;
  }

  getClientsUrl(): string {
    return this.baseUrl + ApiEndpoints.CLIENTS;
  }

  getClientByIdUrl(clientId: string): string {
    return this.getClientsUrl() + '/' + clientId;
  }

  getAllContractsUrl(): string {
    return this.baseUrl + ApiEndpoints.CONTRACTS;
  }

  getContractsUrl(clientId: string): string {
    return this.getClientByIdUrl(clientId) + ApiEndpoints.CONTRACTS;
  }

  getContractByIdUrl(clientId: string, contractId: string): string {
    return this.getContractsUrl(clientId) + '/' + contractId;
  }

  getEmailContactUrl(): string {
    return this.baseUrl + ApiEndpoints.EMAIL_CONTACT + ApiEntities.CONTACT_REQUEST;
  }

  getEmailContractPendingUrl(): string {
    return this.baseUrl + ApiEndpoints.EMAIL + ApiEntities.CONTRACT_PENDING;
  }

  getEmailContractPaidedUrl(): string {
    return this.baseUrl + ApiEndpoints.EMAIL + ApiEntities.CONTRACT_PAIDED;
  }

  getEmailContractPaidedGenerateInvoiceUrl(): string {
    return this.baseUrl + ApiEndpoints.EMAIL + ApiEntities.CONTRACT_PAIDED_GENERATE_INVOICE;
  }

  getEmailContractExpiredUrl(): string {
    return this.baseUrl + ApiEndpoints.EMAIL + ApiEntities.CONTRACT_EXPIRED;
  }

  getEmailContractCancelledUrl(): string {
    return this.baseUrl + ApiEndpoints.EMAIL + ApiEntities.CONTRACT_CANCELLED;
  }

  getEmailSchedulingPendingUrl(): string {
    return this.baseUrl + ApiEndpoints.EMAIL + ApiEntities.SCHEDULING_PENDING;
  }

  getEmailTechnicianSchedulingPendingUrl(): string {
    return this.baseUrl + ApiEndpoints.EMAIL + ApiEntities.TECHNICIAN_SCHEDULING_PENDING;
  }

  getEmailSchedulingConfirmedUrl(): string {
    return this.baseUrl + ApiEndpoints.EMAIL + ApiEntities.SCHEDULING_CONFIRMED;
  }

  getEmailTechnicianSchedulingConfirmedUrl(): string {
    return this.baseUrl + ApiEndpoints.EMAIL + ApiEntities.TECHNICIAN_SCHEDULING_CONFIRMED;
  }

  getEmailSchedulingFinishedUrl(): string {
    return this.baseUrl + ApiEndpoints.EMAIL + ApiEntities.SCHEDULING_FINISHED;
  }

  getEmailSchedulingCancelledUrl(): string {
    return this.baseUrl + ApiEndpoints.EMAIL + ApiEntities.SCHEDULING_CANCELLED;
  }

  getEmailTechnicianSchedulingCancelledUrl(): string {
    return this.baseUrl + ApiEndpoints.EMAIL + ApiEntities.TECHNICIAN_SCHEDULING_CANCELLED;
  }

  private getEupagoUrl(): string {
    return this.baseUrl + ApiEndpoints.EUPAGO;
  }

  getEupagoTransactionsUrl(transactionId: string): string {
    return this.getEupagoUrl() + ApiEntities.TRANSACTIONS + transactionId;
  }

  getEupagoMultibancoUrl(): string {
    return this.getEupagoUrl() + ApiEntities.MULTIBANCO;
  }

  getEupagoMbWayUrl(): string {
    return this.getEupagoUrl() + ApiEntities.MBWAY;
  }

  getFirebaseUrl(): string {
    return this.baseUrl + ApiEndpoints.FIREBASE;
  }

  getFirebaseByIdUrl(firebaseId: string): string {
    return this.getFirebaseUrl() + '/' + firebaseId;
  }

  getFlowsUrl(): string {
    return this.baseUrl + ApiEndpoints.FLOWS;
  }

  getFlowsByIdUrl(flowId: string): string {
    return this.getFlowsUrl() + '/' + flowId;
  }

  getFlowsRollbackUrl(flowId: string): string {
    return this.getFlowsUrl() + '/' + flowId + ApiEntities.ROLLBACK;
  }

  getFlowsVersionsUrl(flowId: string): string {
    return this.getFlowsUrl() + '/' + flowId + ApiEndpoints.VERSIONS;
  }

  getFlowsVersionsByIdUrl(flowId: string, flowVersionId: string): string {
    return this.getFlowsUrl() + '/' + flowId + ApiEntities.VERSIONS + flowVersionId;
  }

  getFlowsPublishUrl(flowId: string, flowVersionId: string): string {
    return this.getFlowsVersionsByIdUrl(flowId, flowVersionId) + ApiEntities.PUBLISH;
  }

  getFlowsInTestingUrl(flowId: string, flowVersionId: string): string {
    return this.getFlowsVersionsByIdUrl(flowId, flowVersionId) + ApiEntities.IN_TESTING;
  }

  getGoogleReviewUrl(): string {
    return this.baseUrl + ApiEndpoints.GOOGLE_REVIEW;
  }

  getSchedulesUrl(): string {
    return this.baseUrl + ApiEndpoints.SCHEDULES;
  }

  getScheduleByIdUrl(scheduleId: string): string {
    return this.getSchedulesUrl() + '/' + scheduleId;
  }

  getSchedulesByStatusForTechnicianUrl(technicianId: string): string {
    return this.getSchedulesUrl() + ApiEntities.TECHNICIAN + technicianId + '/' + ApiEntities.STATUS;
  }

  getSchedulesByDateForTechnicianUrl(technicianId: string): string {
    return this.getSchedulesUrl() + ApiEntities.TECHNICIAN + technicianId + '/' + ApiEntities.BY_DATE;
  }

  getSchedulesForTechnicianUrl(technicianId: string): string {
    return this.getSchedulesUrl() + ApiEntities.TECHNICIAN + technicianId;
  }

  getSchedulesByDateForClientUrl(clientId: string): string {
    return this.getSchedulesUrl() + ApiEntities.CLIENT + clientId + '/' + ApiEntities.BY_DATE;
  }

  getSchedulesForClientUrl(clientId: string): string {
    return this.getSchedulesUrl() + ApiEntities.CLIENT + clientId;
  }

  getServicesUrl(): string {
    return this.baseUrl + ApiEndpoints.SERVICES;
  }

  getServiceByIdUrl(serviceId: string): string {
    return this.getServicesUrl() + '/' + serviceId;
  }

  getTechniciansUrl(): string {
    return this.baseUrl + ApiEndpoints.TECHNICIANS;
  }

  getTechnicianByIdUrl(technicianId: string): string {
    return this.getTechniciansUrl() + '/' + technicianId;
  }

  getUsersUrl(): string {
    return this.baseUrl + ApiEndpoints.USERS;
  }

  getUserByIdUrl(userId: string): string {
    return this.getUsersUrl() + '/' + userId;
  }
}
